import { AreaChart, BarChart } from '@mantine/charts';
import {
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MetricHint, type MetricHintKey } from 'src/secret-menu/billing/MetricHint';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { applyLens, engagementOf, indexSeries, trendOf, type Lens } from './lenses';
import { billingFor, formatPercent, percentOfStated, type RevenueModel } from './revenue';
import { USAGE_PARTS, type Firm, type UsageModel } from './usageModel';
import FirmUsersCard from './FirmUsersCard';
import {
  describePricing,
  PRICING_CATEGORY_COLORS,
  PRICING_CATEGORY_LABELS,
} from '../revenue/pricing';
import { formatRevenue } from '../revenue/ui';

type Props = {
  model: UsageModel;
  firm: Firm;
  month: string;
  lens: Lens;
  today: Date;
  revenue: RevenueModel | null;
  onEditBilling: (detail: BillingDetail) => void;
  onBack: () => void;
};

const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

function Tile({
  label,
  hint,
  value,
  children,
}: {
  label: string;
  hint?: MetricHintKey;
  value: string;
  children?: React.ReactNode;
}) {
  return (
    <Stack
      gap={6}
      p="md"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        background: 'var(--mantine-color-default-hover)',
      }}
    >
      <Group gap={4} wrap="nowrap">
        <Text size="textXs" c="dimmed">
          {label}
        </Text>
        {hint && <MetricHint hint={hint} />}
      </Group>
      <Text size="xl" fw={FONT_WEIGHTS.semibold} style={tabular}>
        {value}
      </Text>
      {children}
    </Stack>
  );
}

function FamilyTile({
  label,
  hint,
  count,
  stated,
  color,
}: {
  label: string;
  hint: MetricHintKey;
  count: number;
  stated: number | null;
  color: string;
}) {
  const percent = percentOfStated(count, stated);
  return (
    <Tile label={label} hint={hint} value={count.toLocaleString('en-GB')}>
      {percent === null ? (
        <Text size="textXs" c="dimmed">
          No stated families to compare with
        </Text>
      ) : (
        <>
          <Progress value={Math.min(percent, 100)} color={color} size="sm" />
          <Text size="textXs" c="dimmed">
            {formatPercent(percent)} of {stated?.toLocaleString('en-GB')} stated
          </Text>
        </>
      )}
    </Tile>
  );
}

const monthYear = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

function RevenueCard({
  detail,
  onEdit,
}: {
  detail: BillingDetail | undefined;
  onEdit: (detail: BillingDetail) => void;
}) {
  if (!detail) {
    return (
      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb={4}>
          Revenue
        </Text>
        <Text size="textXs" c="dimmed">
          No billing record for this firm.
        </Text>
      </Paper>
    );
  }

  const category = detail.pricingCategory;
  const trial = category === 'TRIAL';
  const stated = detail.statedBillableUnits;
  const fullPence = detail.fullAdoptionMonthlyBillPence;
  const billPence = detail.monthlyBillPence ?? 0;

  let pricing = describePricing(detail);
  if (trial) {
    pricing += detail.billingStartDate
      ? ` · starts ${monthYear(detail.billingStartDate)}`
      : ' · no start date';
  }

  return (
    <Paper p="lg" withBorder radius="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={4}>
          <Group gap="xs">
            <Text size="textSm" c="dimmed">
              {trial ? 'Monthly bill once billing starts' : 'Monthly bill'}
            </Text>
            <Badge variant="light" color={PRICING_CATEGORY_COLORS[category]}>
              {PRICING_CATEGORY_LABELS[category]}
            </Badge>
          </Group>
          <Text fz={44} lh={1.1} fw={FONT_WEIGHTS.semibold} style={tabular}>
            {formatRevenue(billPence)}
          </Text>
          <Text size="textSm" c="dimmed">
            {pricing}
          </Text>
        </Stack>
        <Button size="xs" variant="light" onClick={() => onEdit(detail)}>
          Edit billing
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm" mt="lg">
        <Tile
          label="Families stated by the firm"
          hint="statedFamilies"
          value={stated === null ? '—' : stated.toLocaleString('en-GB')}
        >
          {stated === null && (
            <Text size="textXs" c="dimmed">
              Add in Edit billing
            </Text>
          )}
        </Tile>
        <FamilyTile
          label="On Afternoon"
          hint="allUnits"
          count={detail.billableUnitCount}
          stated={stated}
          color="blue"
        />
        <FamilyTile
          label="Billed"
          hint="billedUnits"
          count={detail.activeBillableUnitCount}
          stated={stated}
          color="green"
        />
        <Tile
          label="If every stated family were billed"
          value={fullPence === null ? '—' : formatRevenue(fullPence)}
        >
          {fullPence !== null && fullPence > billPence && (
            <Text size="textXs" c="green">
              +{formatRevenue(fullPence - billPence)} a month on today
            </Text>
          )}
        </Tile>
      </SimpleGrid>
    </Paper>
  );
}

export default function FirmDetail({
  model,
  firm,
  month,
  lens,
  today,
  revenue,
  onEditBilling,
  onBack,
}: Props) {
  const chartLens: Lens = lens === 'share' ? 'raw' : lens;
  const months = model.months.filter(key => key >= firm.first);

  const rawSeries = months.map(key =>
    applyLens(firm.series.get(key)?.usage ?? 0, key, chartLens === 'indexed' ? 'raw' : chartLens, {
      model,
      today,
      firm,
    }),
  );
  const series = chartLens === 'indexed' ? indexSeries(rawSeries) : rawSeries;

  const usageData = months.map((key, index) => ({
    month: model.monthLabels.get(key) ?? key,
    usage: series[index],
  }));

  const counts = firm.series.get(month);
  const breakdownData = USAGE_PARTS.map(item => ({
    metric: item.label,
    value: counts?.[item.key] ?? 0,
  }));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Anchor size="textXs" onClick={onBack}>
            ← Back to all firms
          </Anchor>
          <Title order={3}>{firm.name}</Title>
          <Text size="textXs" c="dimmed">
            First active {model.monthLabels.get(firm.first)} · last active{' '}
            {model.monthLabels.get(firm.last)} · {firm.activeMonths} active months
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge variant="light" color="gray">
            {engagementOf(counts?.usage ?? 0, counts?.newClients ?? 0)}
          </Badge>
          <Badge variant="light">{trendOf(firm, month, model)}</Badge>
        </Group>
      </Group>

      <RevenueCard detail={billingFor(revenue, firm.name)} onEdit={onEditBilling} />

      <FirmUsersCard companyId={billingFor(revenue, firm.name)?.companyId} />

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Paper p="md" withBorder radius="md">
          <Text fw={FONT_WEIGHTS.semibold} mb="sm">
            Usage over time
          </Text>
          <AreaChart
            h={220}
            data={usageData}
            dataKey="month"
            series={[{ name: 'usage', label: 'ABC excluding new clients', color: 'brand.6' }]}
            curveType="linear"
            withDots={false}
            connectNulls={false}
          />
        </Paper>

        <Paper p="md" withBorder radius="md">
          <Text fw={FONT_WEIGHTS.semibold} mb="sm">
            What they use
          </Text>
          <Text size="textXs" c="dimmed" mb="sm">
            {model.monthLabels.get(month)}
          </Text>
          <BarChart
            h={200}
            data={breakdownData}
            dataKey="metric"
            orientation="vertical"
            series={[{ name: 'value', label: 'Actions', color: 'brand.6' }]}
          />
        </Paper>
      </SimpleGrid>

      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb="sm">
          Month by month
        </Text>
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Month</Table.Th>
                <Table.Th ta="right">ABC excluding new clients</Table.Th>
                <Table.Th ta="right">New clients added</Table.Th>
                {USAGE_PARTS.map(item => (
                  <Table.Th key={item.key} ta="right">
                    {item.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...months].reverse().map(key => {
                const monthCounts = firm.series.get(key);
                return (
                  <Table.Tr key={key}>
                    <Table.Td>{model.monthLabels.get(key)}</Table.Td>
                    <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                      {(monthCounts?.usage ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    <Table.Td ta="right">
                      {(monthCounts?.newClients ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    {USAGE_PARTS.map(item => (
                      <Table.Td key={item.key} ta="right">
                        {(monthCounts?.[item.key] ?? 0).toLocaleString('en-GB')}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
